import { useRef } from 'react';
import type React from 'react';

import { useRestoreFocusOnClose } from '#src/app/shared/hooks/focus';
import type { ShareAction } from '#src/app/shared/hooks/share';

import { Drafting } from '../Drafting';
import { Preview } from '../Preview';
import { ContactDecisionMakerShareSheet } from '../ShareSheet';
import { StoryPick } from '../StoryPick';
import { StoryWrite } from '../StoryWrite';
import { Success } from '../Success';
import type { ContactDecisionMakerFlowModel, ContactDecisionMakerTarget } from '../types';
import { isContactDecisionMakerFlowOnScreen } from '../types';

type Props = {
	decisionMaker: ContactDecisionMakerTarget;
	petitionId: string;
	petitionTitle: string;
	flow: ContactDecisionMakerFlowModel;
	/**
	 * Share links for the post-send sheet, from `useContactDecisionMakerSuccessShareLinks`. Fetched
	 * lazily by the caller, so this is empty until the supporter is close to the success screen.
	 */
	shareActions: readonly ShareAction[];
	/** Whether `shareActions` are still being fetched; the success screen's share CTA waits on it. */
	isShareLoading: boolean;
	restoreFocusRef: React.RefObject<HTMLElement | null>;
};

/**
 * Renders whichever stage of the outreach flow is current.
 *
 * The stages are separate tickets (screens 2–5) but one machine, so this switch is the single place
 * that maps a stage to its screen. Stages own their own `<ComposeSheet>` rather than sharing one
 * mounted instance — see that component for why.
 *
 * The mount (the SPH checklist item today) owns everything page-specific: the entry CTA, the send
 * mutation, completion, and the share actions.
 */
export const ContactDecisionMakerFlow: React.FC<Props> = ({
	decisionMaker,
	petitionId,
	petitionTitle,
	flow,
	shareActions,
	isShareLoading,
	restoreFocusRef,
}) => {
	// Focus restoration lives here, not in `ComposeSheet`. Each stage mounts its own sheet, so a sheet
	// unmounts on every stage change while still `isOpen` — a per-sheet effect either never sees the
	// close (it is unmounted first) or fires on every stage change and yanks focus mid-flow. Keyed on
	// "is any of the flow on screen" instead, this fires exactly once, when the flow actually closes.
	useRestoreFocusOnClose(isContactDecisionMakerFlowOnScreen(flow.data.stage), restoreFocusRef);

	// The share sheet layers over the success screen, so closing it must return focus to that screen's
	// heading. `restoreFocusRef` points at the entry CTA, which is behind the overlay.
	const successHeadingRef = useRef<HTMLHeadingElement | null>(null);

	const stageProps = { decisionMaker, petitionId, petitionTitle, flow, restoreFocusRef };

	switch (flow.data.stage) {
		case 'story:pick':
			return <StoryPick {...stageProps} />;

		case 'story:write':
			return <StoryWrite {...stageProps} />;

		case 'drafting':
			return <Drafting {...stageProps} />;

		// One screen with three states: read-only, editing, and in flight. Keeping `sending` here is what
		// lets a retryable failure drop the supporter back onto their own draft instead of a dead end.
		case 'preview':
		case 'preview:editing':
		case 'sending':
			return <Preview {...stageProps} />;

		// The share sheet layers over the success screen rather than replacing it, so dismissing it
		// reveals the confirmation again.
		case 'success':
		case 'share':
			return (
				<>
					<Success {...stageProps} headingRef={successHeadingRef} isShareLoading={isShareLoading} />
					<ContactDecisionMakerShareSheet
						isOpen={flow.data.stage === 'share'}
						onOpenChange={(isOpen) => {
							if (!isOpen) flow.actions.closeShare();
						}}
						shareActions={shareActions}
						restoreFocusRef={successHeadingRef}
					/>
				</>
			);

		// `idle` has nothing open; `contacted` is the entry surface's own state (screen 6), not a sheet.
		case 'idle':
		case 'contacted':
			return null;

		default:
			return null;
	}
};
